import React, { useState, useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftAvatar from "components/SoftAvatar";
import swal from "sweetalert2";
import bruce from "assets/images/bruce-mars.jpg";
import { api } from "utils/Api";
import EditFormPopup from "layouts/admin/user/data/EditForm";
import DeleteForm from "layouts/admin/Organization/DeleteForm";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SmartTableCard from "utils/Smart Table/SmartTableCard";
import ManagerAddPropertyPopup from "./ManagerAddToProperty";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { Box, Tooltip } from "@mui/material";
import { useHierarchy } from "context/HierarchyContext";
import { useMemo } from "react";
import RemoveFromParentForm from "../Organization/MultipleDeleteForm";
import { showError } from "utils/messageAlerts";
import { showSuccess } from "utils/messageAlerts";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { getSecureItem } from "utils/SecureStorage";
import MultiConfirmDialog from "utils/MultiConfirmDialog";

function Managers() {
  const [managers, setManagers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isManagerAddPopupOpen, setIsManagerAddPopupOpen] = useState(false);
  const [popupMode, setPopupMode] = useState("create");
  const [selectedManager, setSelectedManager] = useState(null);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const { orgId } = useHierarchy();
  const [propertyFilterAnchor, setPropertyFilterAnchor] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [propertySearch, setPropertySearch] = useState("");
  const roleId = JSON.parse(getSecureItem("currentUser"))?.role_id;
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    manager: null,
    property: null
  });

  useEffect(() => {
    fetchManagers();
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const propertyData = await api.su_Property.getSuProperties({ organization_id: parseInt(orgId) });
      setProperties(propertyData);
    } catch (e) {
    }
  }; 

  // Fetch all users, then filter only role_id === 3 (Managers)
  const fetchManagers = async () => {
    let role_id = 3; // filtering for Managers (role_id 3)
    try {
      const userData = await api.getFiltertedUser.getFiltertedUser({ role_id, organization_id: parseInt(orgId) });

      const formatted = userData.map((u) => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email,
        phone_number: u.phone_number,
        description: u.description,
        active: u.active,
        image: u.image || null,
        organization_id: u.organization_id,
        role_id: u.role_id,
        createdAt: u.created_at,
        property_id: u.property_id,
        user_property: u.user_property || null,
      }));
      setManagers(formatted);
    } catch (e) {
      setManagers([]);
    }
  };

  // Edit Manager
  const handleEditManager = (manager) => {
    setPopupMode("edit");
    setSelectedManager(manager);
    setIsPopupOpen(true);
  };

  // Delete Manager
  const handleDeleteManager = async (payload) => {
    try {
      await api.su_Property.deleteUserFromProperty(payload);

      showSuccess("Manager removed from property successfully");

      fetchManagers();
    } catch (err) {
      
    }
  };

  // Search Managers
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const propertyMap = useMemo(() => {
    const map = {};
    properties.forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [properties]);

  const filteredManagers = managers.filter((manager) => {
    const term = searchTerm.toLowerCase();

    const matchesSearch =
      !searchTerm.trim() ||
      manager.first_name?.toLowerCase().includes(term) ||
      manager.last_name?.toLowerCase().includes(term) ||
      manager.email?.toLowerCase().includes(term) ||
      manager.username?.toLowerCase().includes(term);

    const matchesProperty =
      selectedProperties.length === 0 ||
      manager.user_property?.some((up) => selectedProperties.includes(up.property_id));

    return matchesSearch && matchesProperty;
  });

  const filteredPropertyList = properties
    .filter((prop) => prop.organization_id === Number(orgId)) // 👈 filter by orgId
    .filter((prop) => prop.name.toLowerCase().includes(propertySearch.toLowerCase()));

  const handleConfirmAction = async (property_id) => {
      const { manager, type } = confirmDialog;
  
      if (!property_id) return;
  
      try {
        if (type === "delete") {
          await api.su_Property.hardDeletePropertyUser({
            property_id,
            user_id: manager.id,
            user_role_id: 3,
            organization_id: orgId,
          });
          showSuccess("Manager permanently deleted successfully");
        } else {
          await api.su_Property.restorePropertyUser({
            property_id,
            organization_id: orgId,
            current_user_role_id: roleId,
            user_id: manager.id,
          });
          showSuccess("Manager restored successfully");
        }

        fetchManagers();
      } catch (e) {}
  
      setConfirmDialog({ open: false, type: null, manager: null, property: null });
    };

  return (
    <SmartTableCard
      title={`All Managers`}
      headerRight={[
        // <SoftButton
        //   key="add-btn"
        //   variant="outlined"
        //   color="info"
        //   size="small"
        //   onClick={() => {
        //     setPopupMode("create");
        //     setSelectedManager(null);
        //     setIsPopupOpen(true);
        //   }}
        // >
        //   New Manager
        // </SoftButton>,

        <SoftButton
          key="add-btn"
          variant="outlined"
          color="info"
          size="small"
          onClick={() => setIsManagerAddPopupOpen(true)}
        >
          Add Manager to Property
        </SoftButton>,

        <SoftInput
          key="search-input"
          placeholder="Search Manager"
          value={searchTerm}
          onChange={handleSearch}
          size="small"
        />,
      ]}
      columns={[
        // { label: "Manager", key: "Manager" },
        { label: "Username", key: "Username", align: "center" },
        { label: "Email", key: "Email", align: "center" },
        { label: "Phone", key: "Phone", align: "center" },
        { label: "Description", key: "Description", align: "center" },
        // { label: "Property", key: "PropertyID", align: "center" },
        {
          label: (
            <SoftBox display="flex" alignItems="center" justifyContent="center" gap="4px">
              <span>Property</span>

              <SoftBox position="relative" display="flex" alignItems="center">
                <FilterAltOutlinedIcon
                  fontSize="small"
                  sx={{
                    cursor: "pointer",
                    color: selectedProperties.length ? "#0284C7" : "#475467",
                    "&:hover": { color: "#0284C7" },
                  }}
                  onClick={(e) => setPropertyFilterAnchor(e.currentTarget)}
                />

                {selectedProperties.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -6,
                      background: "#0284C7",
                      color: "#fff",
                      borderRadius: "50%",
                      fontSize: 10,
                      width: 16,
                      height: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {selectedProperties.length}
                  </Box>
                )}
              </SoftBox>
            </SoftBox>
          ),
          key: "PropertyID",
          align: "center",
        },
        { label: "Active", key: "Active", align: "center" },
        { label: "Actions", key: "Actions", align: "center" },
      ]}
      rows={filteredManagers.map((manager) => {
  
        const validUserProperties = (manager.user_property || []).filter(
          (uu) => propertyMap?.[uu.property_id]
        );

        const isActive = validUserProperties.some((uu) => uu.active === true);
        const isInactive = !isActive;

        return {
        Username: manager.username,
        Email: manager.email,
        Phone: manager.phone_number,
        Description: manager.description,
        // PropertyID: properties.find((org) => org.id === manager.property_id)?.name || "N/A",
        PropertyID: (() => {
          if (!manager.user_property?.length) return "N/A";

          const propertyNames = manager.user_property
            .filter((up) => up.user_id === manager.id)
            .map((up) => propertyMap[up.property_id])
            .filter(Boolean);

          return propertyNames.length ? propertyNames.join(", ") : "N/A";
        })(),
        Active: (() => {
          const inactiveProperty = manager.user_property?.find((uu) => uu.active === false);
          const activeProperty = manager.user_property?.find((uu) => uu.active === true);

          if (inactiveProperty) {
            return (
              <SoftBox display="flex" justifyContent="center" gap="6px">
                {/* Restore */}
                <Tooltip title="Restore Manager" arrow>
                  <IconButton
                    onClick={() =>
                      setConfirmDialog({
                        open: true,
                        type: "restore",
                        manager,
                        property: inactiveProperty,
                      })
                    }
                    sx={{ color: "#16A34A" }}
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Delete */}
                <Tooltip title="Delete Permanently" arrow>
                  <IconButton
                    onClick={() =>
                      setConfirmDialog({
                        open: true,
                        type: "delete",
                        manager,
                        property: inactiveProperty,
                      })
                    }
                    sx={{ color: "#DC2626" }}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </SoftBox>
            );
          }

          // ✅ Otherwise show active
          if (activeProperty) {
            return (
              <SoftTypography variant="button" color="success" fontWeight="bold">
                Yes
              </SoftTypography>
            );
          }

          return "N/A";
        })(),
        Actions: (
          <SoftBox display="flex" justifyContent="center" gap="8px">
            <IconButton
              onClick={() => handleEditManager(manager)}
              disabled={isInactive}
              sx={{
                border: "1px solid #D0D5DD",
                backgroundColor: "#F9FAFB",
                width: 34,
                height: 34,
                "& svg": { fontSize: 18 },
                color: "#475467",
                "&:hover": {
                  backgroundColor: "#E0F2FE",
                  borderColor: "#38BDF8",
                  color: "#0284C7",
                },
              }}
            >
              <EditIcon fontSize="sm" />
            </IconButton>

            <IconButton
              onClick={() => {
                setDeletePopupOpen(true);
                setSelectedManager(manager);
              }}
              disabled={isInactive}
              sx={{
                border: "1px solid #FECACA",
                backgroundColor: "#FEF2F2",
                width: 34,
                height: 34,
                "& svg": { fontSize: 18 },
                color: "#B91C1C",
                "&:hover": {
                  backgroundColor: "#FEE2E2",
                  borderColor: "#F87171",
                  color: "#991B1B",
                },
              }}
            >
              <DeleteIcon fontSize="sm" />
            </IconButton>
          </SoftBox>
        ),
      };
      })}
    >
      <MultiConfirmDialog
              open={confirmDialog.open}
              type={confirmDialog.type}
              title={confirmDialog.type === "delete" ? "Delete Manager" : "Restore Manager"}
              description={
                confirmDialog.type === "delete"
                  ? "This will permanently delete the manager. This action cannot be undone."
                  : "Are you sure you want to restore this manager?"
              }
              confirmText={confirmDialog.type === "delete" ? "Delete Permanently" : "Restore"}
              onClose={() => setConfirmDialog({ open: false, type: null, manager: null, property: null })}
              onConfirm={handleConfirmAction}
              assignments={confirmDialog.manager?.user_property || []}
              parentMap={propertyMap}
              idKey="property_id"
              entityLabel="Property"
            />

      {isPopupOpen && (
        <EditFormPopup
          mode={popupMode}
          user={selectedManager}
          onClose={() => {
            setIsPopupOpen(false);
            fetchManagers();
          }}
        />
      )}

      {isManagerAddPopupOpen && (
        <ManagerAddPropertyPopup
          onClose={() => setIsManagerAddPopupOpen(false)}
          refreshData={fetchManagers}
        />
      )}

      {deletePopupOpen && (
        // <RemoveFromParentForm
        //   data={selectedManager}
        //   onClose={() => setDeletePopupOpen(false)}
        //   onDelete={(payload) => {
        //     handleDeleteManager(payload);
        //     setDeletePopupOpen(false);
        //   }}
        // />
        <RemoveFromParentForm
          open={deletePopupOpen}
          entity={selectedManager}
          entityLabel="Manager"
          parentLabel="Property"
          assignments={selectedManager?.user_property}
          parentKey="property_id"
          parentMap={propertyMap}
          onClose={() => setDeletePopupOpen(false)}
          onRemove={(property_id, user) => {
            handleDeleteManager({
              property_id,
              user_id: user.id,
              user_role_id: 3,
              organization_id: orgId,
            });
            setDeletePopupOpen(false);
          }}
        />
      )}

      <Menu
        anchorEl={propertyFilterAnchor}
        open={Boolean(propertyFilterAnchor)}
        onClose={() => setPropertyFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {/* Search */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={1}
          py={1}
          gap={1}
        >
          <SoftInput
            placeholder="Search property"
            size="small"
            value={propertySearch}
            onChange={(e) => setPropertySearch(e.target.value)}
          />

          <IconButton
            size="small"
            onClick={() => {
              setPropertyFilterAnchor(null);
              setPropertySearch(""); // optional: reset search
            }}
          >
            ✕
          </IconButton>
        </Box>

        {/* Clear */}
        {selectedProperties.length > 0 && (
          <MenuItem
            onClick={() => setSelectedProperties([])}
            sx={{ color: "#0284C7", fontWeight: 600 }}
          >
            Clear Selection
          </MenuItem>
        )}

        {/* Property List */}
        {filteredPropertyList.length > 0 ? (
          filteredPropertyList.map((prop) => (
            <MenuItem
              key={prop.id}
              onClick={() => {
                if (selectedProperties.includes(prop.id)) {
                  setSelectedProperties(selectedProperties.filter((id) => id !== prop.id));
                } else {
                  setSelectedProperties([...selectedProperties, prop.id]);
                }
              }}
            >
              <Checkbox checked={selectedProperties.includes(prop.id)} />
              <ListItemText primary={prop.name} primaryTypographyProps={{ variant: "body3" }} />
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No properties found</MenuItem>
        )}
      </Menu>
    </SmartTableCard>
  );
}

export default Managers;